import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '@chakra-ui/react'
import { AgentForm, FormData } from '../components/forms/AgentForm'
import { useAgent } from '../hooks/agents/useAgent'
import { useLanguage } from '../hooks/utility/useLanguage'
import { UpdateAgentData } from '../api/modules/agentApi'
import { Agent } from '../types/agentTypes'

const EditAgentPage = () => {
  const params = useParams<{ id?: string }>()
  const id = params.id || ''
  const navigate = useNavigate()
  const toast = useToast()
  const { t } = useLanguage()
  const { agent, isLoadingAgent, agentError, updateAgent, deleteAgent, isUpdatingAgent, isDeletingAgent } = useAgent(id)

  // Handle agent loading error
  if (agentError) {
    toast({
      title: t('common.error'),
      description: t('editAgent.failedToLoad'),
      status: 'error',
      duration: 5000,
      isClosable: true,
    })
    navigate('/library', { replace: true })
    return null
  }

  const handleSubmit = async (data: FormData) => {
    if (!agent) return

    try {
      const { fileSearchEnabled, ...rest } = data
      const updateData: UpdateAgentData = {
        id: agent.id,
        ...rest,
        // Preserve the agent's existing type so editing a privileged
        // (sidebar) agent doesn't silently demote it to a normal one.
        type: agent.type,
        tools: fileSearchEnabled ? ['FILE_SEARCH'] : [],
      }
      const result: Agent = await updateAgent(updateData)
      toast({
        title: t('editAgent.agentUpdated'),
        description: `${result.name} ${t('editAgent.updatedSuccessfully')}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      navigate('/library', { replace: true })
    } catch (error) {
      toast({
        title: t('editAgent.updateError'),
        description: t('editAgent.updateErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      throw error // Re-throw to let the form handle the error state
    }
  }

  const handleDelete = async () => {
    if (!agent) return

    try {
      await deleteAgent(agent.id)
      toast({
        title: t('editAgent.agentDeleted'),
        description: `${agent.name} ${t('editAgent.deletedSuccessfully')}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      navigate('/library', { replace: true })
    } catch (error) {
      toast({
        title: t('editAgent.deleteError'),
        description: t('editAgent.deleteErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      throw error // Re-throw to let the form handle the error state
    }
  }

  // Prepare initial data for the form
  const initialData = agent ? {
    name: agent.name || '',
    description: agent.description || '',
    tags: agent.tags.map(tag => tag.name) || [],
    system_prompt: agent.system_prompt || '',
    profile_picture_url: agent.profile_picture_url || '',
    example_questions: agent.example_questions || [],
    fileSearchEnabled: (agent.tools ?? []).includes('FILE_SEARCH'),
    tracing_mode: agent.tracing_mode || ('tool_based' as const),
  } : {}

  return (
    <AgentForm
      mode="edit"
      initialData={initialData}
      isLoading={isLoadingAgent}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      isSubmitting={isUpdatingAgent}
      isDeleting={isDeletingAgent}
      title={t('editAgent.title')}
      subtitle={t('editAgent.subtitle')}
      submitButtonText={t('editAgent.updateAgent')}
    />
  )
}

export default EditAgentPage
